/**
 * Goal Facade
 *
 * Stable API surface for Goal operations.
 * Aggregates actions for presentation layer consumption.
 * @agentic connections
 */

import type { MedplumClient } from "@medplum/core";
import type { Goal, Patient, PractitionerRole } from "@medplum/fhirtypes";
import {
  createGoal,
  updateGoal,
  createGoalProgress,
  closeGoal,
} from "@benchmark/core/actions";
import type { GoalFormData, GoalProgressFormData } from "@benchmark/core/types";
import type { GoalAchievementStatus } from "@benchmark/core/constants";

/**
 * Creates a Goal facade instance bound to a MedplumClient.
 * @agentic connections
 */
export function createGoalFacade(medplum: MedplumClient) {
  return {
    /**
     * Create a new goal with baseline observation.
     * @agentic connections
     */
    create: (
      formData: GoalFormData,
      patient: Patient,
      practitionerRole: PractitionerRole,
    ): Promise<Goal> => {
      return createGoal(medplum, {
        formData,
        patient,
        practitionerRole,
      });
    },

    /**
     * Update an existing goal.
     * @agentic connections
     */
    update: (
      formData: GoalFormData,
      existingGoal: Goal,
      patient: Patient,
      practitionerRole: PractitionerRole,
    ): Promise<Goal> => {
      return updateGoal(medplum, {
        formData,
        existingGoal,
        patient,
        practitionerRole,
      });
    },

    /**
     * Record progress for a goal. Goal ID must be in formData.goalId.
     * @agentic connections
     */
    recordProgress: (
      formData: GoalProgressFormData,
      patient: Patient,
      practitionerRole?: PractitionerRole,
    ): Promise<void> => {
      return createGoalProgress(medplum, {
        formData,
        patient,
        practitionerRole,
      }).then(() => {
        // Return void instead of Observation
      });
    },

    /**
     * Close a goal with achievement status.
     * @agentic connections
     */
    close: (
      goal: Goal,
      achievementStatus: GoalAchievementStatus,
      patient: Patient,
      statusDate?: string,
    ): Promise<Goal> => {
      return closeGoal(medplum, {
        goal,
        achievementStatus,
        patient,
        statusDate,
      });
    },
  };
}
